/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent} from 'react';
import {InputBaseComponentProps} from '@material-ui/core/InputBase/InputBase';
import TextField, {StandardTextFieldProps} from '@material-ui/core/TextField';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import {TopNavSearchIcon} from '@ion/dls/src/icons/TopNavSearchIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useExtendStyles} from '../../hooks/useExtendStyles';
import {DLSTheme} from '../../types/types';

const MAX_SEARCH_LENGTH = 32000;

export interface SearchFieldTypes extends StandardTextFieldProps {
  label?: string | null,
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hasError?: boolean,
  errorText?: string,
  optionsProps?: GenericObject,
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  inputComponent?: React.ElementType<InputBaseComponentProps>;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  overrides?: (theme: DLSTheme) => GenericObject;
  externalClasses?: GenericObject;
}

const useStyles = () => ({
  searchFieldRoot : {
    backgroundColor                                     : dlsColor('white'),
    border                                              : `solid 1px ${dlsColor('grey5')}`,
    fontSize                                            : '14px !important',
    height                                              : '48px',
    paddingLeft                                         : '10px !important',
    width                                               : '100%',
    '& input[type="search"]::-webkit-search-decoration' : {
      '-webkit-appearance' : 'none'
    },
    '& input[type="search"]::-webkit-search-cancel-button' : {
      '-webkit-appearance' : 'none'
    },
    '& input[type="search"]::-webkit-search-results-button' : {
      '-webkit-appearance' : 'none'
    },
    '& input[type="search"]::-webkit-search-results-decoration' : {
      '-webkit-appearance' : 'none'
    }
  },
  searchFieldUnderline : {
    '&:before' : {
      border : 'none'
    },
    '&:after' : {
      border : 'none'
    },
    '&:hover' : {
      '&:not(.Mui-disabled)' : {
        '&:before' : {
          border : 'none'
        }
      }
    }
  },
  searchFieldInput : {
    paddingLeft : '5px !important'
  },
  searchFieldError : {
    border : `solid 2px ${dlsColor('caRed')}`
  },
  searchFieldDisabled : {
    '&&:before' : {
      borderBottomStyle : 'none'
    }
  }
});

/**
 * @SearchField
 *
 * Defines the SearchField component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   <SearchField label='Name'
 *     hasError
 *     errorText='Please enter a valid Name' />
 *
 * ```
 */
export const SearchField = ({
  className,
  onChange,
  overrides,
  startAdornment,
  endAdornment,
  inputComponent,
  externalClasses,
  label = null,
  placeholder = '',
  disabled = false,
  hasError = false,
  errorText = '',
  optionsProps = {}
}: SearchFieldTypes) => {
  const searchFieldStyles = useExtendStyles(useStyles, overrides);
  const classes = searchFieldStyles();

  function handleKeyPress(event) {
    if (optionsProps?.detailedDeviceGridSearch && event.key === 'Enter') {
      optionsProps?.onKeyPress(event);
    }
  }

  return (
    <TextField placeholder={placeholder}
      label={label}
      type='search'
      error={hasError}
      value={optionsProps?.value}
      disabled={disabled}
      helperText={hasError && errorText}
      onChange={onChange}
      onKeyPress={handleKeyPress}
      inputProps={{maxLength: MAX_SEARCH_LENGTH}}
      InputProps={{
        classes : {
          root : classnames(
            classes.searchFieldRoot,
            className,
            hasError ? classes.searchFieldError : null
          ),
          underline : classes.searchFieldUnderline,
          disabled  : classes.searchFieldDisabled,
          input     : classnames(classes.searchFieldInput, externalClasses?.input)
        },
        startAdornment : startAdornment || <TopNavSearchIcon />,
        endAdornment   : endAdornment || null,
        inputComponent : inputComponent
      }} />
  );
};

export default SearchField;
